'use client';

import CoursePricing from '@/components/course-card/course-pricing';
import { PromoInputForm } from '@/components/promo';
import SectionTitle from '@/components/section-title/section-title';
import SupportNumber from '@/components/support-number';
import { useFacebookPixelEvents } from '@/hooks/use-facebook-pixel-events';
import { usePathname, useRouter } from '@/i18n/routing';
import { Button } from '@/ui';
import NumberFormatter from '@/utils/bn';
import { checkGenderRestriction } from '@/utils/gender-utils';
import {
  ApiResponse,
  CommonCourseUtils,
  CourseViewModel,
  CurrencyInfo,
  PromoViewModel,
  groupBy,
} from '@hidayahlab/academy-suite-corelib';
import {
  Clock01Icon,
  File01Icon,
  Folder02Icon,
  PlayIcon,
  UserIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { FacebookIcon, TicketIcon, XCircleIcon } from '@tajdid/tajdid-ui/icons';
import { User } from 'lucia';
import { useLocale, useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { getPromoByCode } from '../../../actions/promo-action';
import GenderRestrictionMessage from '../gender-restriction-message';

export type CourseCardProps = {
  isEnrolled: boolean;
  courseDetails: CourseViewModel;
  promoRes?: ApiResponse<PromoViewModel>;
  totalEnrollments?: number;
  user?: User | null;
  currencyInfo: CurrencyInfo;
};

export default function RecordedCourseMetaAction({
  courseDetails,
  isEnrolled,
  promoRes,
  totalEnrollments,
  user,
  currencyInfo,
}: CourseCardProps) {
  const locale = useLocale();
  const t = useTranslations();
  const tPayment = useTranslations('payment-page');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const pathname = usePathname();
  const [promoInputValue, setPromoInputValue] = useState('');
  const [promo, setPromoData] = useState<PromoViewModel | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState('');
  const searchParams = useSearchParams();
  const router = useRouter();
  const { trackAddToCart } = useFacebookPixelEvents();

  const { isGenderAllowed, showGenderMessage } = checkGenderRestriction(
    courseDetails.genderAccess,
    user?.gender,
  );

  const {
    discount,
    price,
    slug,
    curriculum,
    durationInHour,
    isFree,
    discountType = 'PERCENTAGE',
  } = courseDetails;
  let discountPrice = CommonCourseUtils.calculateDiscountedPrice(
    price,
    discount,
    discountType,
  );

  const contents = curriculum?.flatMap(item => item.contents) ?? [];

  const contentGroupByType = groupBy(contents, 'type');

  useEffect(() => {
    if (promoRes?.status === 'success') {
      setPromoData(promoRes.data);
    }
  }, [promoRes]);

  const isPromoApplied = !!searchParams.get('promo');

  if (isPromoApplied && promo) {
    discountPrice = CommonCourseUtils.calculateDiscountedPrice(
      price,
      promo.value,
      promo.type,
    );
  }

  const handleBuyNowButtonClick = () => {
    let path = '';

    if (isEnrolled) {
      path = `/my-classes/recorded/${courseDetails.id}`;
    } else if (promo && isPromoApplied) {
      path = `/payment/${courseDetails.id}/${slug}?promo=${promo.code}`;
    } else {
      trackAddToCart(
        {
          email: user?.email,
          phone: user?.phone,
        },
        {
          value: courseDetails.price,
        },
      );

      path = `/payment/${courseDetails.id}/${slug}`;
    }

    router.push(path);
  };

  const handleApplyPromo = async () => {
    setErrorMessage('');
    const res = await getPromoByCode({
      code: promoInputValue,
      courseId: courseDetails.id,
    });

    if (res.status === 'success') {
      setPromoData(res.data);
      router.push(pathname + '?' + `promo=${res.data.code}` + '#course_card');
      setShowPromoInput(false);
    } else {
      setErrorMessage('The promo is not valid');
    }
  };

  return (
    <div className="top-[90px] bg-white rounded-md md:shadow-sm h-fit md:mt-0 md:sticky">
      <div className="px-4 py-6 md:px-6 md:py-8">
        <SectionTitle size="md">
          {t('course-details.courseBenefitsCard.card-title')}
        </SectionTitle>

        <div className="pt-4 md:pt-8">
          <div className="flex gap-3 justify-around items-center lg:gap-4">
            <div className="flex pl-[1%] lg:pl-[6%] items-center w-[49%] gap-2 ">
              <div className="p-2">
                <HugeiconsIcon
                  icon={UserIcon}
                  className="text-primary-600 size-5"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600 capitalize">
                  {t('course-details.courseBenefitsCard.completed')}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {locale === 'bn'
                    ? NumberFormatter(totalEnrollments ?? 0)
                    : totalEnrollments}{' '}
                  {t('course-details.courseBenefitsCard.students')}
                </p>
              </div>
            </div>
            <div className="bg-[#e5e5e5] w-[1px] h-[60px] rounded-[32px] border-background"></div>
            <div className="flex pl-[1%] lg:pl-[6%] items-center w-[49%] gap-2 ">
              <div className="p-2">
                <HugeiconsIcon
                  icon={Clock01Icon}
                  className="text-primary-600 size-5"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600 capitalize">
                  {t('course-details.courseBenefitsCard.duration')}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {locale === 'bn'
                    ? NumberFormatter(durationInHour ?? 0)
                    : durationInHour}{' '}
                  {t('course-details.courseBenefitsCard.hours')}
                </p>
              </div>
            </div>
          </div>
          <div className="py-3">
            <div className="w-full h-[1px] border-background-bottom bg-[#e5e5e5]"></div>
          </div>
          <div className="flex gap-3 justify-around items-center lg:gap-4">
            <div className="flex pl-[1%] lg:pl-[6%] items-center w-[49%] gap-2 ">
              <div className="p-2">
                <HugeiconsIcon
                  icon={PlayIcon}
                  className="text-primary-600 size-5"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600 capitalize">
                  {t('course-details.video')}{' '}
                  {t('course-details.courseBenefitsCard.lessons')}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {locale === 'bn'
                    ? NumberFormatter(contentGroupByType.LESSON?.length ?? 0)
                    : (contentGroupByType.LESSON?.length ?? 0)}
                </p>
              </div>
            </div>
            <div className="bg-[#e5e5e5] w-[1px] h-[60px] rounded-[32px] border-background "></div>
            <div className="flex pl-[1%] lg:pl-[6%] items-center w-[49%] gap-2 ">
              <div className="p-2">
                <HugeiconsIcon
                  icon={Folder02Icon}
                  className="text-primary-600 size-5"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600 capitalize">
                  {t('course-details.courseBenefitsCard.document')}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {locale === 'bn'
                    ? NumberFormatter(contentGroupByType.RECOURSE?.length ?? 0)
                    : (contentGroupByType.RECOURSE?.length ?? 0)}
                </p>
              </div>
            </div>
          </div>
          <div className="py-3">
            <div className="w-full h-[1px] border-background-bottom bg-[#e5e5e5]"></div>
          </div>
          <div className="flex gap-3 justify-around items-center lg:gap-4">
            <div className="flex pl-[1%] lg:pl-[6%] items-center w-[49%] gap-2 ">
              <div className="p-2">
                <HugeiconsIcon
                  icon={File01Icon}
                  className="text-primary-600 size-5"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600 capitalize">
                  {t('course-details.courseBenefitsCard.total-quiz')}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {locale === 'bn'
                    ? NumberFormatter(contentGroupByType.QUIZ?.length ?? 0)
                    : (contentGroupByType.QUIZ?.length ?? 0)}
                </p>
              </div>
            </div>
            <div className="bg-[#e5e5e5] w-[1px] h-[60px] rounded-[32px] border-background"></div>
            <div className="flex pl-[1%] lg:pl-[6%] items-center w-[49%] gap-2 ">
              <div className="p-2">
                <FacebookIcon className="text-primary-600 size-5" />
              </div>
              <div>
                <p className="text-sm text-gray-600 capitalize">
                  {t('course-details.courseBenefitsCard.support-group')}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {t('course-details.courseBenefitsCard.facebook')}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="fixed bottom-0 left-0 z-50 px-4 py-1 pb-4 w-full bg-gray-50 md:static md:bg-transparent md:px-0 md:pb-0 md:py-1">
          <div className="hidden py-0 md:py-8 md:flex">
            <div className="w-full border-b border-gray-200"></div>
          </div>
          {!isEnrolled && (
            <div className="flex justify-between items-center lg:mb-5">
              <CoursePricing
                price={price}
                discountPrice={discountPrice}
                discount={discount}
                isFree={isFree}
                currencyInfo={currencyInfo}
              />
              {!isPromoApplied && discount !== 100 && (
                <Button
                  variant="link"
                  className="flex gap-2 items-center text-base font-semibold cursor-pointer"
                  icon={<TicketIcon />}
                  iconPosition="start"
                  onClick={() => setShowPromoInput(true)}
                >
                  {tPayment('apply-promo-code')}
                </Button>
              )}
            </div>
          )}
          {isPromoApplied && promo && !errorMessage && !isEnrolled ? (
            <div className="flex justify-between items-center bg-gray-50 rounded-sm">
              <div className="flex gap-1 items-center">
                <TicketIcon className="text-primary-700" />
                <p className="text-base font-medium">{promo?.code}</p>
                <p>প্রোমো এপ্লাইড</p>
              </div>
              <Button
                onClick={() => {
                  router.push(pathname + '#course_card');
                  setPromoInputValue('');
                }}
                variant="icon"
                color="gray"
                icon={<XCircleIcon className="w-5 h-5" />}
              />
            </div>
          ) : (
            showPromoInput && (
              <div className="flex flex-col gap-2 mt-4">
                <div className="grid grid-cols-5 gap-2 items-center lg:grid-cols-7">
                  <div className="col-span-4 lg:col-span-6">
                    <PromoInputForm
                      label="প্রোমো কোড"
                      placeholder="আপনার প্রোমো কোডটি লিখুন"
                      onChange={e => setPromoInputValue(e.target.value)}
                      handleApplyPromo={handleApplyPromo}
                    />
                  </div>
                  <div className="col-span-1">
                    <Button
                      onClick={() => {
                        setShowPromoInput(false);
                        setErrorMessage('');
                      }}
                      className="px-2 mt-5 h-10 lg:w-full min-w-12 lg:h-12"
                      variant="outlined"
                      color="gray"
                    >
                      <XCircleIcon />
                    </Button>
                  </div>
                </div>
                <div>
                  {errorMessage && (
                    <p className="text-error-500">{errorMessage}</p>
                  )}
                </div>
              </div>
            )
          )}
          <GenderRestrictionMessage
            showGenderMessage={showGenderMessage}
            isGenderAllowed={isGenderAllowed}
            genderAccess={courseDetails.genderAccess}
            locale={locale}
            className="mt-2"
          />

          <div className="mt-2">
            {!isGenderAllowed ? (
              <Button
                className="rounded-md bg-primary-500"
                fullWidth
                disabled={true}
              >
                {isEnrolled ? t('button.continue') : t('button.buy-the-course')}
              </Button>
            ) : (
              <Button
                className="rounded-md bg-primary-500"
                fullWidth
                onClick={handleBuyNowButtonClick}
              >
                {isEnrolled ? t('button.continue') : t('button.buy-the-course')}
              </Button>
            )}
          </div>
        </div>

        <SupportNumber className="mt-4 max-md:hidden" />
      </div>
    </div>
  );
}
